import { supabase } from '@/integrations/supabase/client';
import { 
  BotNameParsed, 
  ExitSignal, 
  DataPoint, 
  GroupPerformanceData,
  PerformanceAnalyzeResponse,
  PerformanceGroup 
} from '@/types/performance-analyze';
import { format, subDays, eachDayOfInterval, parseISO } from 'date-fns';

export class PerformanceAnalyzeService {
  /**
   * Parse bot name to extract components
   * Handles: "❗ XAUUSD-0.15_0.15-SW-10S" or "XAUUSD-0.2_0.2-TR-10S"
   */
  static parseBotName(botName: string): BotNameParsed | null {
    try {
      // Remove leading/trailing whitespace
      const trimmed = botName.trim();
      
      // Check if starts with emoji/special char (non-alphanumeric, not space)
      const firstChar = trimmed[0];
      const hasPrefix = firstChar && !/[a-zA-Z0-9\s]/.test(firstChar);
      
      let prefix: string | null = null;
      let rest = trimmed;
      
      if (hasPrefix) {
        prefix = firstChar.trim();
        rest = trimmed.slice(1).trim(); // Remove prefix and any following space
      }
      
      // Parse the rest: SYMBOL-TP_SL-STRATEGY-TIMEFRAME
      // Pattern: (SYMBOL)-(TP)_(SL)-(TR|SW)-(TIMEFRAME)
      const pattern = /^([\w.]+)-([\d.]+)_([\d.]+)-(TR|SW)-(\w+)$/;
      const match = rest.match(pattern);
      
      if (!match) {
        console.warn('Bot name does not match expected pattern:', botName);
        return null;
      }
      
      return {
        prefix,
        symbol: match[1],
        tp: parseFloat(match[2]),
        sl: parseFloat(match[3]),
        strategy: match[4] as 'TR' | 'SW',
        timeframe: match[5]
      };
    } catch (error) {
      console.error('Error parsing bot name:', botName, error);
      return null;
    }
  }

  /**
   * Get all EXIT signals for given bot IDs
   */
  static async getExitSignals(
    botIds: string[],
    startDate?: string,
    endDate?: string
  ): Promise<ExitSignal[]> {
    try {
      let query = supabase
        .from('conditional_skip_events')
        .select('id, bot_id, action, signal_timestamp, response_status, win, payload')
        .in('bot_id', botIds)
        .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
        .eq('response_status', 200)
        .order('signal_timestamp', { ascending: true });

      if (startDate) {
        query = query.gte('signal_timestamp', startDate);
      }
      if (endDate) {
        query = query.lte('signal_timestamp', endDate);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      return data || [];
    } catch (error) {
      console.error('Error fetching exit signals:', error);
      throw error;
    }
  }

  /**
   * Calculate rolling winrate for signals
   */
  static calculateRollingWinrate(
    signals: ExitSignal[],
    windowSizeDays: number,
    startDate: string,
    endDate: string
  ): DataPoint[] {
    const dataPoints: DataPoint[] = [];
    
    // Get all days in range
    const days = eachDayOfInterval({
      start: parseISO(startDate),
      end: parseISO(endDate)
    });

    for (const day of days) {
      const dayStr = format(day, 'yyyy-MM-dd');
      const windowStart = format(subDays(day, windowSizeDays - 1), 'yyyy-MM-dd');
      
      // Get signals in window
      const signalsInWindow = signals.filter(signal => {
        const signalDate = format(parseISO(signal.signal_timestamp), 'yyyy-MM-dd');
        return signalDate >= windowStart && signalDate <= dayStr;
      });

      if (signalsInWindow.length === 0) {
        dataPoints.push({
          date: dayStr,
          winrate: 0,
          total_signals: 0,
          winning_signals: 0
        });
        continue;
      }

      // Calculate winrate using the win column (1 = win, 0 = loss)
      const winningSignals = signalsInWindow.filter(signal => signal.win === 1).length;

      const winrate = (winningSignals / signalsInWindow.length) * 100;

      dataPoints.push({
        date: dayStr,
        winrate: parseFloat(winrate.toFixed(2)),
        total_signals: signalsInWindow.length,
        winning_signals: winningSignals
      });
    }

    return dataPoints;
  }

  /**
   * Get bot IDs matching group criteria
   */
  static async getBotsForGroup(groupId: string): Promise<string[]> {
    try {
      // Get group config
      const { data: group, error: groupError } = await supabase
        .from('performance_analyze_groups')
        .select('*')
        .eq('id', groupId)
        .single();

      if (groupError) throw groupError;
      if (!group) return [];

      // Get all Type 2 bots with conditional skip settings
      const { data: bots, error: botsError } = await supabase
        .from('bots')
        .select(`
          id,
          name,
          conditional_skip_settings!inner(
            skip_type
          )
        `)
        .eq('conditional_skip_settings.skip_type', 'FLEXIBLE_PATTERN');

      if (botsError) throw botsError;
      if (!bots) return [];

      // Filter bots based on parsed name
      const matchingBotIds: string[] = [];

      for (const bot of bots) {
        const parsed = this.parseBotName(bot.name);
        if (!parsed) continue;

        // Normalize prefixes for comparison (trim both sides)
        const normalizedGroupPrefix = group.prefix?.trim() || null;
        const normalizedParsedPrefix = parsed.prefix?.trim() || null;

        // Check if matches group criteria
        const symbolMatch = parsed.symbol === group.symbol;
        const prefixMatch = normalizedParsedPrefix === normalizedGroupPrefix;
        const strategyMatch = parsed.strategy === group.strategy;
        const tpMatch = (group.tp_values as number[]).includes(parsed.tp);

        if (symbolMatch && prefixMatch && strategyMatch && tpMatch) {
          matchingBotIds.push(bot.id);
        }
      }

      return matchingBotIds;
    } catch (error) {
      console.error('Error getting bots for group:', error);
      throw error;
    }
  }

  /**
   * Main API function to get performance data
   */
  static async getPerformanceData(
    groupIds: string[],
    windowSize: number,
    startDate?: string,
    endDate?: string
  ): Promise<PerformanceAnalyzeResponse> {
    try {
      // Default date range if not provided
      const end = endDate || format(new Date(), 'yyyy-MM-dd');
      const start = startDate || format(subDays(new Date(), 30), 'yyyy-MM-dd');

      // Get groups
      const { data: groups, error: groupsError } = await supabase
        .from('performance_analyze_groups')
        .select('*')
        .in('id', groupIds);

      if (groupsError) throw groupsError;
      if (!groups) return { symbols: [], window_size: windowSize, date_range: { start, end }, groups: [] };

      const groupPerformanceData: GroupPerformanceData[] = [];
      const symbols = new Set<string>();

      for (const group of groups) {
        symbols.add(group.symbol);

        // Get bot IDs for this group
        const botIds = await this.getBotsForGroup(group.id);
        
        if (botIds.length === 0) {
          // No bots found, add empty data
          groupPerformanceData.push({
            group_id: group.id,
            group_name: `${group.symbol}-${group.prefix || 'No Prefix'}-${group.strategy}-${group.size_group}`,
            prefix: group.prefix,
            strategy: group.strategy,
            size_group: group.size_group,
            tp_values: group.tp_values as number[],
            data_points: []
          });
          continue;
        }

        // Get exit signals for these bots
        const signals = await this.getExitSignals(botIds, start, end);

        // Calculate rolling winrate
        const dataPoints = this.calculateRollingWinrate(signals, windowSize, start, end);

        groupPerformanceData.push({
          group_id: group.id,
          group_name: `${group.symbol}-${group.prefix || 'No Prefix'}-${group.strategy}-${group.size_group}`,
          prefix: group.prefix,
          strategy: group.strategy,
          size_group: group.size_group,
          tp_values: group.tp_values as number[],
          data_points: dataPoints
        });
      }

      return {
        symbols: Array.from(symbols),
        window_size: windowSize,
        date_range: { start, end },
        groups: groupPerformanceData
      };
    } catch (error) {
      console.error('Error getting performance data:', error);
      throw error;
    }
  }
}
